import { campaignCopy, type CampaignPermissions } from '@discovr/features';
import { fmtDateTime } from '../../lib/dateUtils';
import type {
  AcceptanceRollbackFromStatus,
  CampaignPipeline,
  Post,
  PostStatus,
  RollbackFromStatus,
  RosterStatus,
} from '@discovr/types';
import { initialPostStatus, resolvePipeline } from '../utils/campaignPipeline';

const copy = campaignCopy.creator.hold;

export const LIVE_HOLD_BLOCKED = copy.liveBlocked;

// A released post keeps its history but has an empty reason_code
export function isPostHeld(post?: Post): boolean {
  return !!post?.hold?.reason_code;
}

const HOLDABLE_STAGES: ReadonlySet<string> = new Set([
  'script_pending',
  'script_review',
  'content_pending',
  'content_review',
  'live_pending',
]);

export const HOLD_NOT_AVAILABLE = copy.notAvailable;
export const ALREADY_ON_HOLD = copy.alreadyOnHold;
export const HELD_ACTION_REASON = copy.heldActionReason;

export function holdBlockedReason(post: Post | undefined): string | null {
  if (post?.status === 'live') return LIVE_HOLD_BLOCKED;
  if (!post || !HOLDABLE_STAGES.has(post.status ?? '')) return HOLD_NOT_AVAILABLE;
  return isPostHeld(post) ? ALREADY_ON_HOLD : null;
}

export function canPutOnHold(
  post: Post | undefined,
  permissions: Pick<CampaignPermissions, 'canHoldCreator'> | undefined,
): boolean {
  return !!permissions?.canHoldCreator && holdBlockedReason(post) === null;
}

export function holdDateLabel(at?: string): string {
  if (!at || Number.isNaN(new Date(at).getTime())) return '';
  return fmtDateTime(at);
}

export function heldAtLabel(post?: Post): string {
  return holdDateLabel(post?.hold?.held_at);
}

const rollbackCopy = campaignCopy.creator.rollback;

type RollbackLabels = { from: string; to: string; menu: string };

export type RollbackTarget =
  | {
      kind: 'acceptance';
      from: AcceptanceRollbackFromStatus;
      to: Extract<RosterStatus, 'accepted_by_brand'>;
      labels: RollbackLabels;
    }
  | {
      kind: 'script' | 'content';
      from: RollbackFromStatus;
      to: Extract<PostStatus, 'script_review' | 'content_review'>;
      labels: RollbackLabels;
    };

const labelsFor = ({ from, to, menu }: RollbackLabels): RollbackLabels => ({ from, to, menu });

// Exactly one rollback per post: the pipeline's initial stage goes back to Brand
// approved, and no rollback lands in a review stage the pipeline has turned off.
export function rollbackTarget(post: Post | undefined, pipeline?: CampaignPipeline | null): RollbackTarget | null {
  const status = post?.status;
  if (!status) return null;
  const initial = initialPostStatus(pipeline);
  if (status === initial) {
    return { kind: 'acceptance', from: initial, to: 'accepted_by_brand', labels: labelsFor(rollbackCopy.acceptanceStage) };
  }
  const stages = resolvePipeline(pipeline);
  switch (status) {
    case 'content_pending':
      return stages.script_enabled
        ? { kind: 'script', from: status, to: 'script_review', labels: labelsFor(rollbackCopy.stages[status]) }
        : null;
    case 'live_pending':
      return stages.content_enabled
        ? { kind: 'content', from: status, to: 'content_review', labels: labelsFor(rollbackCopy.stages[status]) }
        : null;
    default:
      return null;
  }
}

export function rollbackMenuBlockedReason(post: Post | undefined, isWired: boolean): string | null {
  if (!isWired) return copy.actionUnavailable;
  return isPostHeld(post) ? rollbackCopy.holdBlocked : null;
}
